// src/components/communications/CommunicationHistory.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  FaPhoneAlt, 
  FaEnvelope, 
  FaCommentDots,
  FaSort, 
  FaSortAmountDown, 
  FaSortAmountUp,
  FaFilter,
  FaTimes
} from 'react-icons/fa';
import CallItem from './CallItem';
import MessageItem from './MessageItem';
import EmailItem from './EmailItem';
import { getLeadCommunicationHistory } from '../../services/communicationService';
import LoadingSpinner from '../shared/LoadingSpinner';

const Container = styled.div`
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(3)};
`;

const Title = styled.h3`
  margin: 0;
  color: ${props => props.theme.colors.textPrimary};
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(2)};
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
  background-color: ${props => props.isActive ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.isActive ? props.theme.colors.textPrimary : props.theme.colors.textSecondary};
  border: 1px solid ${props => props.isActive ? props.theme.colors.primary : props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.isActive ? props.theme.colors.primary : props.theme.colors.primary + '22'};
    color: ${props => props.isActive ? props.theme.colors.textPrimary : props.theme.colors.primary};
  }
`;

const SortButton = styled(FilterButton)`
  padding: ${props => props.theme.spacing(1)};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing(6)};
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  text-align: center;
  
  svg {
    font-size: 2rem;
    color: ${props => props.theme.colors.inputGray};
    margin-bottom: ${props => props.theme.spacing(2)};
  }
  
  p {
    color: ${props => props.theme.colors.textSecondary};
    font-size: 1rem;
    margin: 0;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${props => props.theme.spacing(6)};
`;

const CommunicationHistory = ({ leadId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'calls', 'messages', 'emails'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc', 'desc'
  
  const fetchHistory = async () => {
    try {
      setLoading(true);
      const data = await getLeadCommunicationHistory(leadId);
      setHistory(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching communication history:', err);
      setError('Failed to load communication history. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (leadId) {
      fetchHistory();
    }
  }, [leadId]);
  
  const getFilteredAndSortedHistory = () => {
    let filteredHistory = [...history];
    
    // Apply type filter
    if (filter === 'calls') {
      filteredHistory = filteredHistory.filter(item => item.type === 'call');
    } else if (filter === 'messages') {
      filteredHistory = filteredHistory.filter(item => item.type === 'sms');
    } else if (filter === 'emails') {
      filteredHistory = filteredHistory.filter(item => item.type === 'email');
    }
    
    // Apply sorting
    filteredHistory.sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    });
    
    return filteredHistory;
  };
  
  const filteredHistory = getFilteredAndSortedHistory();
  
  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }
  
  if (error) {
    return (
      <EmptyState>
        <FaTimes />
        <p>{error}</p>
      </EmptyState>
    );
  }
  
  if (!history.length) {
    return (
      <EmptyState>
        <FaEnvelope />
        <p>No communication history found for this lead.</p>
      </EmptyState>
    );
  }
  
  return (
    <Container>
      <Header>
        <Title>Communication History</Title>
        <Controls>
          <FilterGroup>
            <FilterButton 
              isActive={filter === 'all'} 
              onClick={() => setFilter('all')}
            >
              All
            </FilterButton>
            <FilterButton 
              isActive={filter === 'calls'} 
              onClick={() => setFilter('calls')}
            >
              <FaPhoneAlt /> Calls
            </FilterButton>
            <FilterButton
              isActive={filter === 'messages'}
              onClick={() => setFilter('messages')}
            >
              <FaCommentDots /> Messages
            </FilterButton>
            <FilterButton
              isActive={filter === 'emails'}
              onClick={() => setFilter('emails')}
            >
              <FaEnvelope /> Emails
            </FilterButton>
          </FilterGroup>
          
          <SortButton 
            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
          >
            {sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
          </SortButton>
        </Controls>
      </Header>
      
      {filteredHistory.length === 0 ? (
        <EmptyState>
          <FaFilter />
          <p>No {filter === 'calls' ? 'calls' : filter === 'messages' ? 'messages' : filter === 'emails' ? 'emails' : 'communication'} found with current filters.</p>
        </EmptyState>
      ) : (
        filteredHistory.map(item => (
          item.type === 'call' ? (
            <CallItem
              key={item.id}
              call={item}
              refreshHistory={fetchHistory}
            />
          ) : item.type === 'email' ? (
            <EmailItem
              key={item.id}
              email={item}
            />
          ) : (
            <MessageItem
              key={item.id}
              message={item}
            />
          )
        ))
      )}
    </Container>
  );
};

export default CommunicationHistory;